import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Typography, IconButton } from '@material-ui/core';
import BackIcon from '@material-ui/icons/ArrowBack';
import { useNavigate } from 'react-router';
import { useSelector } from 'react-redux';
import cartIcon from './cart.png';
import './restaurant-header.scss';
import { LocalizerHelper } from '../../helpers/localizerHelper';
import { RootState } from '../../store';
import { auth } from '../../firebase';
import { getAvatarPlaceholder } from '../../helpers/firebaseStorageHelper';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import ExitToAppIcon from '@material-ui/icons/ExitToApp';
import Avatar from '@material-ui/core/Avatar';

interface RestaurantHeaderProps {
    className?: string;
    showBack?: boolean;
    showCart?: boolean;
    onClickCart?: () => void;
    onClickBack?: () => void;
}

const _RestaurantHeader: React.FC<RestaurantHeaderProps> = ({
    className = '',
    showBack = true,
    showCart = false,
    onClickCart,
    onClickBack
}) => {
    const navigate = useNavigate();

    const restaurant = useSelector((state: RootState) => state.restaurant.restaurant);
    const session = useSelector((state: RootState) => state.session.session);
    const menu = useSelector((state: RootState) => state.menu.menu);
    const sessionMenuItems = useSelector((state: RootState) => state.session.menuItems);
    const userPhotoUrl = useSelector((state: RootState) => state.auth.firestoreUser?.photoURL);
    const userNickname = useSelector((state: RootState) => state.auth.firestoreUser?.nickname);
    const isAnonymous = useSelector(() => auth.currentUser?.isAnonymous ?? true);
    const [avatarPlaceholder, setAvatarPlaceholder] = useState<string>('');

    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

    useEffect(() => {
        getAvatarPlaceholder().then(setAvatarPlaceholder);
    }, []);

    const restaurantHasLogo = useMemo(() => {
        return !!restaurant?.logo?.url && restaurant.logo.url.length > 0;
    }, [restaurant]);

    const restaurantLogo = useMemo(() => restaurant?.logo?.url, [restaurant]);
    const restaurantInfo = useMemo(() => restaurant?.business_additional_information, [restaurant]);

    const handleClickCart = () => onClickCart?.();

    const handleClickBack = () => {
        if (onClickBack) onClickBack();
        else navigate(-1);
    };

    const handleAvatarClick = (e: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(e.currentTarget);
    };

    const handleCloseMenu = () => setAnchorEl(null);

    const handleLogout = async () => {
        handleCloseMenu();
        await auth.signOut();
        const segments = location.pathname.split('/').filter(Boolean);
        // segments[0] = 'quid' or 'menu', segments[1] = the id
        const homepage = `/${segments[0]}/${segments[1]}`;
        
        navigate(homepage, { replace: true });
    };

    const menuName = LocalizerHelper.localized(menu, ['name']);
    const tableName = LocalizerHelper.localized(session, ['table_name']);

    const showUserIcon = !isAnonymous;

    return (
        <div>
            <div className="header-row-generic">
                <div className="header-left">
                    {showBack && (
                        <IconButton
                            aria-label="back"
                            size="medium"
                            className="back-button"
                            onClick={handleClickBack}
                        >
                            <BackIcon fontSize="inherit" />
                        </IconButton>
                    )}
                </div>

                <div className="header-center">
                    {restaurantHasLogo ? (
                        <img src={restaurantLogo} alt="restaurant logo" />
                    ) : (
                        <Typography variant="h5" className="page-header">
                            {LocalizerHelper.localized(restaurant ?? {}, ['name'])}
                        </Typography>
                    )}
                </div>

                <div className="header-right">
                    {showUserIcon && (
                        <>
                            <Avatar
                                src={userPhotoUrl ?? avatarPlaceholder}
                                alt="user avatar"
                                className="user-avatar"
                                onClick={handleAvatarClick}
                                imgProps={{
                                    onError: (e) => {
                                        (e.target as HTMLImageElement).src = avatarPlaceholder;
                                    }
                                }}
                            />

                            <Menu
                                anchorEl={anchorEl}
                                open={Boolean(anchorEl)}
                                onClose={handleCloseMenu}
                                getContentAnchorEl={null}
                                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                                PaperProps={{
                                    style: { borderRadius: 12, minWidth: 180, marginTop: 8 }
                                }}
                            >
                                <div className="user-menu-header">
                                    <Avatar
                                        src={userPhotoUrl ?? avatarPlaceholder}
                                        className="user-menu-avatar"
                                    />
                                    <Typography className="user-menu-nickname">
                                        {userNickname ?? ''}
                                    </Typography>
                                </div>
                                <MenuItem onClick={handleLogout} className="user-menu-item--danger">
                                    <ExitToAppIcon fontSize="small" style={{ marginRight: 10 }} />
                                    Logout
                                </MenuItem>
                            </Menu>
                        </>
                    )}
                    
                    {showCart && sessionMenuItems.length > 0 && (
                        <div className="cart-icon-container" onClick={handleClickCart}>
                            <div className="cart-badge">{sessionMenuItems.length}</div>
                            <img src={cartIcon} className="cart-icon" alt="cart" />
                        </div>
                    )}
                </div>
            </div>
            {restaurantInfo && restaurantInfo.length > 0 && 
                <div className="container subheader">
                    <div className="row">
                        <div className="col-12 text-center">
                            <span className="restaurant-info-label">
                                {restaurantInfo}
                            </span>
                        </div>
                    </div>
                </div>
            }
            {tableName && menuName && (
                <div className="container subheader">
                    <div className="row">
                        <div className="col-12 text-center">
                            <span className="delivery-mode-label">
                                {`${tableName} (${menuName})`}
                            </span>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default _RestaurantHeader;